import React, { useState } from 'react';
import './Contact.css';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: 'web',
    budget: '',
    message: '',
    website: '' // Honeypot field
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitTime, setSubmitTime] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Honeypot check - if filled, it's a bot
    if (formData.website && formData.website.trim() !== '') {
      console.log('Bot detected - honeypot filled with:', formData.website);
      return;
    }

    // Check if form was filled too quickly (less than 3 seconds)
    if (submitTime && Date.now() - submitTime < 3000) {
      setError('Please slow down and try again.');
      return;
    }

    // Rate limiting - check last submission time
    const lastSubmit = localStorage.getItem('lastFormSubmit');
    if (lastSubmit && Date.now() - parseInt(lastSubmit) < 60000) {
      setError('Please wait a minute before submitting again.');
      return;
    }

    // Basic validation
    if (formData.name.length < 2 || formData.message.length < 10) {
      setError('Please provide valid name and message (at least 10 characters).');
      return;
    }

    setIsSubmitting(true);

    // Send data to PHP backend
    const formBody = new URLSearchParams();
    formBody.append('name', formData.name);
    formBody.append('email', formData.email);
    formBody.append('phone', formData.phone);
    formBody.append('projectType', formData.projectType);
    formBody.append('budget', formData.budget);
    formBody.append('message', formData.message);

    fetch('/send-email.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formBody.toString()
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Store submission time
        localStorage.setItem('lastFormSubmit', Date.now().toString());
        
        setSubmitted(true);
        setIsSubmitting(false);
        
        setTimeout(() => {
          setSubmitted(false);
          setFormData({
            name: '',
            email: '',
            phone: '',
            projectType: 'web',
            budget: '',
            message: '',
            website: ''
          });
          setSubmitTime(null);
        }, 5000);
      } else {
        setError('Failed to send message. Please try again or contact us directly.');
        setIsSubmitting(false);
      }
    })
    .catch(error => {
      console.error('Error:', error);
      setError('Failed to send message. Please try again or contact us directly.');
      setIsSubmitting(false);
    });
  };

  // Track when user starts interacting with form
  const handleFirstInteraction = () => {
    if (!submitTime) {
      setSubmitTime(Date.now());
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Let's Build Together</h2>
          <p className="section-subtitle">
            Have a project in mind? We'd love to hear about it. Fill out the form below 
            and we'll get back to you within 24 hours.
          </p>
        </div>

        <div className="contact-content">
          <div className="contact-info">
            <div className="info-card">
              <div className="info-icon">📧</div>
              <h3>Email Us</h3>
              <p>tarpindergrewal@createmyapp.ca</p>
              <p className="info-detail">We reply within 24 hours</p>
            </div>

            <div className="info-card">
              <div className="info-icon">📱</div>
              <h3>Call Us</h3>
              <p>604-773-1594</p>
              <p className="info-detail">Mon-Fri, 9am-6pm PST</p>
            </div>

            <div className="info-card">
              <div className="info-icon">📍</div>
              <h3>Visit Us</h3>
              <p>Surrey, BC</p>
              <p className="info-detail">Canada</p>
            </div>

            <div className="social-links">
              <h3>Follow Us</h3>
              <div className="social-icons">
                <a href="#" className="social-icon" aria-label="LinkedIn">💼</a>
                <a href="#" className="social-icon" aria-label="Twitter">🐦</a>
                <a href="#" className="social-icon" aria-label="GitHub">💻</a>
                <a href="#" className="social-icon" aria-label="Instagram">📸</a>
              </div>
            </div>
          </div>

          <div className="contact-form-container">
            {submitted ? (
              <div className="success-message">
                <div className="success-icon">✓</div>
                <h3>Thank You!</h3>
                <p>Your message has been sent successfully. We'll get back to you soon.</p>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit} onFocus={handleFirstInteraction}>
                {error && (
                  <div className="error-message">
                    <span className="error-icon">⚠️</span>
                    {error}
                  </div>
                )}
                
                {/* Honeypot field - hidden from users, visible to bots */}
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="honeypot"
                  tabIndex="-1"
                  autoComplete="new-password"
                  aria-hidden="true"
                  placeholder=""
                />
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="604-773-1594"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="projectType">Project Type *</label>
                    <select
                      id="projectType"
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleChange}
                      required
                    >
                      <option value="web">Web Application</option>
                      <option value="ios">iOS App</option>
                      <option value="android">Android App</option>
                      <option value="cross-platform">Cross-Platform App</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="budget">Budget Range</label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                    >
                      <option value="">Select Budget</option>
                      <option value="5k-10k">$5,000 - $10,000</option>
                      <option value="10k-25k">$10,000 - $25,000</option>
                      <option value="25k-50k">$25,000 - $50,000</option>
                      <option value="50k+">$50,000+</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Project Details *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    placeholder="Tell us about your project, goals, and timeline..."
                  ></textarea>
                </div>

                <button type="submit" className="submit-button" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <span className="button-arrow">{isSubmitting ? '⏳' : '→'}</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default Contact;
